/**
 * EAA-1810: the OIDC endpoint set an external app's engineers need, derived
 * from the realm issuer the portal itself runs against (`config.authority`).
 *
 * WHY DERIVE RATHER THAN STORE: there is no field on `Application` (or anywhere
 * in the registry) that holds these, and there should not be — they are facts
 * about the REALM, identical for every app, and inventing per-app columns for
 * them would let them drift from the realm the portal is actually pointed at.
 *
 * `discovery` is the only one an app strictly needs, and it is the only one that
 * is standard OIDC rather than Keycloak-shaped: a correctly built client fetches
 * it and reads the rest from the document. The remaining four follow Keycloak's
 * fixed `/protocol/openid-connect/*` layout — safe to show because the portal's
 * authority IS a Keycloak realm everywhere this console runs — and exist for the
 * common case of a team wiring a library that wants them spelled out. They are
 * presented under the discovery URL, never instead of it.
 */
export interface OidcEndpoints {
    discovery: string;
    authorization: string;
    token: string;
    userinfo: string;
    jwks: string;
    endSession: string;
}

export function oidcEndpointsFor(authority: string): OidcEndpoints {
    // A trailing slash on the authority would produce `//protocol/...`, which
    // some proxies answer with a redirect and some with a 404.
    const base = authority.replace(/\/+$/, '');
    const oidc = `${base}/protocol/openid-connect`;
    return {
        discovery: `${base}/.well-known/openid-configuration`,
        authorization: `${oidc}/auth`,
        token: `${oidc}/token`,
        userinfo: `${oidc}/userinfo`,
        jwks: `${oidc}/certs`,
        endSession: `${oidc}/logout`,
    };
}
